import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, BellSlash, Eye, EyeSlash } from '@phosphor-icons/react';
import SettingRow from './shell/SettingRow';
import { usePushNotifications } from '../lib/usePushNotifications';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Notification controls for the dropdown menu: whether to receive pushes at
 * all, and — once subscribed — whether they may name the sender and quote the
 * message on the lock screen.
 *
 * Renders nothing when the browser can't do Web Push (e.g. iOS Safari before
 * the app is installed to the home screen).
 */
const PushToggle = () => {
  const { supported, permission, subscribed, busy, enable, disable } = usePushNotifications();
  const [previews, setPreviews] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);

  const loadPreviews = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/push/previews`, { withCredentials: true });
      setPreviews(!!data.enabled);
    } catch (e) {
      // Leave it showing "hidden" — that matches the server's own default.
    }
  }, []);

  useEffect(() => {
    if (subscribed) loadPreviews();
  }, [subscribed, loadPreviews]);

  if (!supported) return null;

  const onToggleSubscription = async (e) => {
    e.preventDefault();
    if (subscribed) {
      const r = await disable();
      if (r.ok) toast('Notifications turned off');
      return;
    }
    if (permission === 'denied') {
      toast.error('Notifications are blocked in your browser settings');
      return;
    }
    const r = await enable();
    if (r.ok) toast.success('Notifications enabled');
    else if (r.reason === 'denied') toast.error('Notifications are blocked in your browser settings');
    else toast.error('Could not enable notifications');
  };

  const onTogglePreviews = async (e) => {
    e.preventDefault();
    const next = !previews;
    setPreviewBusy(true);
    try {
      await axios.post(
        `${API_URL}/api/push/previews`, { enabled: next }, { withCredentials: true },
      );
      setPreviews(next);
      toast(next
        ? 'Notifications will show who wrote and what they said'
        : 'Notifications will only say a message arrived');
    } catch (err) {
      toast.error('Could not change that setting');
    } finally {
      setPreviewBusy(false);
    }
  };

  return (
    <>
      <SettingRow
        icon={subscribed ? Bell : BellSlash}
        label="Notifications"
        hint="On this device"
        trailing={subscribed ? 'On' : 'Off'}
        onSelect={onToggleSubscription}
        disabled={busy}
        aria-pressed={subscribed}
        data-testid="push-toggle"
      />
      {subscribed && (
        <SettingRow
          icon={previews ? Eye : EyeSlash}
          label="Message previews"
          hint={previews ? 'Notifications show who wrote and what they said' : 'Notifications only say a message arrived'}
          trailing={previews ? 'Shown' : 'Hidden'}
          onSelect={onTogglePreviews}
          disabled={previewBusy}
          aria-pressed={previews}
          data-testid="push-preview-toggle"
        />
      )}
    </>
  );
};

export default PushToggle;
